/**
 * Admin route guard.
 *
 * This is a UX affordance only — it decides what this tab renders, nothing
 * more. It is not, and must never be treated as, the security boundary: a
 * signed-in non-admin who edits sessionStorage can still make it render.
 * The actual gate is server-side (every /admin/* endpoint checks
 * cognito:groups again) and, for MFA, Cognito's own TOTP challenge on
 * sign-in. This component only saves a non-admin the confusion of landing
 * on a console full of API calls that will 403.
 *
 * The token-reading (isAdmin/getPlatformRole/hasRole) lives in
 * @charisol-spark/client-core, shared with packages/frontend's Sidebar,
 * which uses the same read-only check to decide whether to render the
 * "Admin console" link at all — re-exported here so this app's other
 * components (AdminLayout) keep one import path for both the guard and the
 * role helpers.
 */

import { type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { isAdmin } from "@charisol-spark/client-core";

// eslint-disable-next-line react-refresh/only-export-components -- re-exporting plain functions alongside the AdminGuard component, same pattern the pre-extraction file used.
export {
  isAdmin,
  getPlatformRole,
  hasRole,
  getCurrentUserEmail,
} from "@charisol-spark/client-core";

/**
 * Route guard component — redirects non-admin users to the admin app's own
 * login page (there is no "/app" here; this app has nothing but the console).
 *
 * A direct visit with a signed-in, non-admin session (e.g. a stale tab, or
 * a bookmarked admin URL) carries the same `reason=no_role` LoginPage shows
 * right after such a sign-in — otherwise this path would still silently
 * bounce to a blank login page. "No token at all" (never signed in here)
 * gets the plain login page instead; there's nothing to explain.
 */
export function AdminGuard({ children }: { children: ReactNode }) {
  if (!isAdmin()) {
    const hasToken = !!sessionStorage.getItem("spark_id_token");
    return (
      <Navigate to={hasToken ? "/login?reason=no_role" : "/login"} replace />
    );
  }
  return <>{children}</>;
}
